/**
 * QuickActions.jsx - Premium Ghost Buttons
 * Botoes de acao rapida quando DeviceRow esta expandido
 *
 * Design: Ghost buttons com emojis/icons e hover accent colorido
 * Visual: "🔔 Eventos" "📊 Histórico" "➤ Cmds" "✏️ Editar"
 * Referencia: DeviceListPrototype.html
 *
 * Eventos: Abre EventMapPage com eventos de hoje no mapa
 * Cmds: Abre CommandModal premium para envio de comandos
 */

import { memo, useState, useCallback } from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useNavigate } from 'react-router-dom';
import { Send, NotificationsActive } from '@mui/icons-material';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';
import CommandModal from './CommandModal';

// Emojis para cada acao
const ACTION_EMOJIS = {
  history: '📊',
  edit: '✏️',
};

// Cores especificas por acao
const actionColors = {
  events: '#ef4444',   // Vermelho (alarmes/eventos)
  history: '#3b82f6',  // Azul
  commands: '#f59e0b', // Ambar
  edit: '#8b949e',     // Cinza
};

const useStyles = makeStyles()((theme, { tokens }) => ({
  // Container - 4 botões em 1 linha
  container: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 4,
  },
  // Ghost button compacto
  button: {
    padding: '6px 8px',
    fontSize: '0.7rem',
    fontWeight: 500,
    color: tokens.text.secondary,
    backgroundColor: 'transparent',
    border: `1px solid ${tokens.border.default}`,
    borderRadius: 6,
    textTransform: 'none',
    minWidth: 'auto',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    whiteSpace: 'nowrap',
    '&:hover': {
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  buttonEvents: {
    '&:hover': {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      borderColor: 'rgba(239, 68, 68, 0.4)',
      color: actionColors.events,
    },
  },
  buttonHistory: {
    '&:hover': {
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      borderColor: 'rgba(59, 130, 246, 0.4)',
      color: actionColors.history,
    },
  },
  buttonCommands: {
    '&:hover': {
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      borderColor: 'rgba(245, 158, 11, 0.4)',
      color: actionColors.commands,
    },
  },
  buttonEdit: {
    '&:hover': {
      backgroundColor: 'rgba(139, 148, 158, 0.1)',
      borderColor: 'rgba(139, 148, 158, 0.4)',
      color: actionColors.edit,
    },
  },
}));

const QuickActions = memo(({ deviceId, deviceName, position, onAction }) => {
  const tokens = usePremiumTokens();
  const { classes, cx } = useStyles({ tokens });
  const navigate = useNavigate();
  const t = useTranslation();

  // Command Modal state
  const [commandModalOpen, setCommandModalOpen] = useState(false);

  // Get blocked status from position attributes
  const getBlockedState = useCallback(() => {
    if (!position?.attributes) return false;
    const val = position.attributes.blocked;
    if (typeof val === 'boolean') return val;
    if (typeof val === 'string') return val.toLowerCase() === 'true' || val === '1';
    if (typeof val === 'number') return val === 1;
    return false;
  }, [position]);

  const blocked = getBlockedState();

  // Events handler - navega para EventMapPage com eventos de hoje
  const handleEvents = useCallback((e) => {
    e.stopPropagation();
    // Período: hoje (00:00 até agora)
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    const from = startOfDay.toISOString();
    const to = now.toISOString();
    navigate(`/eventmap?deviceId=${deviceId}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
    if (onAction) onAction('events', deviceId);
  }, [navigate, deviceId, onAction]);

  // History handler
  const handleHistory = useCallback((e) => {
    e.stopPropagation();
    navigate(`/replay?deviceId=${deviceId}`);
    if (onAction) onAction('history', deviceId);
  }, [navigate, deviceId, onAction]);

  // Commands handler
  const handleCommands = useCallback((e) => {
    e.stopPropagation();
    setCommandModalOpen(true);
  }, []);

  const handleCommandModalClose = useCallback(() => {
    setCommandModalOpen(false);
  }, []);

  const handleCommandSuccess = useCallback(() => {
    if (onAction) onAction('commands', deviceId);
  }, [onAction, deviceId]);

  // Edit handler
  const handleEdit = useCallback((e) => {
    e.stopPropagation();
    navigate(`/settings/device/${deviceId}`);
    if (onAction) onAction('edit', deviceId);
  }, [navigate, deviceId, onAction]);

  return (
    <>
      <Box className={classes.container}>
        {/* Eventos */}
        <Tooltip title={t('reportEvents') || 'Ver Eventos de Hoje'} arrow placement="top">
          <Button
            className={cx(classes.button, classes.buttonEvents)}
            onClick={handleEvents}
          >
            <NotificationsActive sx={{ fontSize: 14, mr: 0.5 }} /> Eventos
          </Button>
        </Tooltip>

        {/* Histórico */}
        <Tooltip title={t('reportReplay') || 'Ver Histórico'} arrow placement="top">
          <Button
            className={cx(classes.button, classes.buttonHistory)}
            onClick={handleHistory}
          >
            {ACTION_EMOJIS.history} Histórico
          </Button>
        </Tooltip>

        {/* Comandos */}
        <Tooltip title={t('commandTitle') || 'Enviar Comandos'} arrow placement="top">
          <Button
            className={cx(classes.button, classes.buttonCommands)}
            onClick={handleCommands}
          >
            <Send sx={{ fontSize: 14, mr: 0.5 }} /> {t('commandTitle')}
          </Button>
        </Tooltip>

        {/* Editar */}
        <Tooltip title={t('sharedEdit') || 'Editar Dispositivo'} arrow placement="top">
          <Button
            className={cx(classes.button, classes.buttonEdit)}
            onClick={handleEdit}
          >
            {ACTION_EMOJIS.edit} Editar
          </Button>
        </Tooltip>
      </Box>

      {/* Command Modal - Premium */}
      <CommandModal
        open={commandModalOpen}
        onClose={handleCommandModalClose}
        deviceId={deviceId}
        deviceName={deviceName}
        blocked={blocked}
        onSuccess={handleCommandSuccess}
      />
    </>
  );
});

QuickActions.displayName = 'QuickActions';
export default QuickActions;
